'use client'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faMessageDots as StatusIcon, faRefresh,
  faTowerBroadcast as CommunicationIcon
} from '@fortawesome/pro-regular-svg-icons'
import RenderFormWithRHF from 'src/components/era-package/forms/RenderFormWithRHF'
import DataTableSection from 'src/components/era-package/forms/DataTableSection'
import ResponseModal from './ResponseModal'
import {
  status,
  communication,
  communicationData,
  response,
  optionsMap
} from 'src/views/abi/fields/status-response'
import AbiEntrySummaryResponseApiUrls from 'src/apiUrls/AbiEntrySummaryResponseApiUrls'
import AbiEntrySummaryResponseHistoryApiUrls from 'src/apiUrls/AbiEntrySummaryResponseHistoryApiUrls'
import {Box, Button} from "@mui/material";
import {generatePath} from "react-router-dom";
import pathMap from "../../../../paths/pathMap";
import {useHistory} from "react-router-dom/cjs/react-router-dom";

const StatusResponse = ({ basic, id, displayBtn }) => {

  const history = useHistory()

  // Modal state for response display
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false)
  const [selectedResponseData, setSelectedResponseData] = useState(null)
  const [newStatus, setNewStatus] = useState(status)
  const [newCommunicationData, setNewCommunicationData] = useState(communicationData)
  const [statusReady, setStatusReady] = useState(false)
  const [entryDone, setEntryDone] = useState(false)
  const [historyDone, setHistoryDone] = useState(false)
  const [modalStatusReady, setModalStatusReady] = useState(false)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    communication: newCommunicationData.map((item, index) => ({
      ...item,
      _originalIndex: index
    }))
  })

  const fetchData = () => {
    setLoading(true)
    setStatusReady(false)
    setEntryDone(false)
    setHistoryDone(false)

    fetch(AbiEntrySummaryResponseApiUrls.GET + `?abiTransId=${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization:
          'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      }
    })
      .then((res) => res.json())
      .then((response) => {
        const updatedStatus = newStatus.map((field) => {
          if (field.name === 'entrySummary') {
            return {
              ...field,
              defaultValue: {
                ...field.defaultValue,
                value: response.entrySummaryStatus,
                color:
                  response.entrySummaryStatus === 'Accepted'
                    ? 'success'
                    : 'error'
              }
            }
          }

          if (field.name === 'cargoRelease') {
            return {
              ...field,
              defaultValue: {
                ...field.defaultValue,
                value: response.cargoReleaseStatus,
                color:
                  response.cargoReleaseStatus === 'Accepted'
                    ? 'success'
                    : 'error'
              }
            }
          }

          if (field.name === 'releaseDate') {
            const rawDate = response.releaseDate
            return {
              ...field,
              defaultValue: rawDate ? rawDate : ''
            }
          }

          if (field.name === '1Usg') {
            const rawDate = response.usg1
            return {
              ...field,
              defaultValue: rawDate
                ? rawDate // or .toISOString()
                : ''
            }
          }
          return field
        })
        setNewStatus(updatedStatus)
        setEntryDone(true)
      })

    fetch(AbiEntrySummaryResponseHistoryApiUrls.GET + `?abiTransId=${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization:
          'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      }
    })
      .then((res) => res.json())
      .then((response) => {
        const communicationDataList = []
        response.forEach((item) => {
          let communicationType = ''
          if (item.messageType === 'SO')
            communicationType = 'Cargo Release SN'
          else if (item.messageType === 'SX')
            communicationType = 'Cargo Release'
          else if (item.messageType === 'AX')
            communicationType = 'Entry Summary'
          communicationDataList.push({
            communicationType,
            submit:
              item.messageType === 'AX'
                ? `Action: ${item.action} [${
                  item.submitTime ? item.submitTime : ''
                }]`
                : item.submitTime
                  ? item.submitTime
                  : '',
            status: item.messageDesc,
            time: item.responseTime ? item.responseTime : '',
            oid: item.oid
          })
        })

        setNewCommunicationData(communicationDataList)
        setHistoryDone(true)
        setLoading(false)
      })
  }

  useEffect(() => {
    if (id != null && id != undefined) {
      fetchData()
    }
  }, [id])

  useEffect(() => {
    if (entryDone && historyDone) {
      setStatusReady(true)
    }
  }, [entryDone, historyDone])

  useEffect(() => {
    const updatedData = {}

    newStatus.forEach((field) => {
      updatedData[field.name] = field.defaultValue
    })

    updatedData.communication = newCommunicationData.map((item, index) => ({
      ...item,
      _originalIndex: index
    }))

    setFormData(updatedData)
  }, [newStatus, newCommunicationData])

  const allFields = useMemo(() => {
    return [
      ...newStatus,
      {
        name: 'communication',
        type: 'table',
        required: false,
        defaultValue: formData.communication
      }
    ]
  }, [newStatus, formData.communication])

  const updateFormData = useCallback((newData) => {
    setFormData((prevFormData) => {
      const updatedData =
        typeof newData === 'function' ? newData(prevFormData) : newData
      return updatedData
    })
  }, [])

  // Handle communication row click to show response modal
  const handleCommunicationRowClick = useCallback((rowData, rowIndex) => {
    try {
      fetch(
        AbiEntrySummaryResponseHistoryApiUrls.GET_DETAIL +
          `?oId=${rowData.oid}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization:
              'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
          }
        }
      )
        .then((res) => res.json())
        .then((response) => {
          // Get corresponding response data using the original 1-1 positional relationship
          if (response) {
            // replace special chars
            response.response = response && response.response
              ? response.response
                .replaceAll('&lt;', '<')
                .replaceAll('&gt;', '>')
                .replaceAll('&amp;', '&')
              : '';
            setSelectedResponseData(response)
            setTimeout(() => {
              setIsResponseModalOpen(true)
            }, 0) // let React remount the modal
          } else {
            console.warn(
              `No response data found for original index: ${rowIndex}`
            )
          }
        })
    } catch (err) {
      console.error('Fetch error', err)
    } finally {
      setModalStatusReady(true)
    }
  }, [])

  // Set up form sections
  const formSections = useMemo(() => {
    return [
      {
        title: 'Status',
        icon: <FontAwesomeIcon icon={StatusIcon} />,
        fields: newStatus,
        readOnly: true
      },
      {
        title: 'Communication',
        name: 'communication',
        icon: <FontAwesomeIcon icon={CommunicationIcon} />,
        type: 'table',
        tableComponent: DataTableSection,
        tableData: formData.communication || [],
        tableFields: communication,
        disableEdit: true, // Disable editing
        onRowClick: handleCommunicationRowClick, // Add custom row click handler
        onTableDataChange: (newData) =>
          updateFormData((prev) => ({ ...prev, communication: newData }))
      }
    ]
  }, [newStatus, formData, updateFormData, handleCommunicationRowClick])

  const handleEdit = () => history.push(generatePath(pathMap.ABI_ENTRY_SUMMARY_EDIT_VIEW, { id }));

  return (
    <>
      {statusReady && (
        <RenderFormWithRHF
          title='Status & Response'
          formSections={formSections}
          allFields={allFields}
          optionsMap={optionsMap}
          submitLabel={false}
          readOnly
          basicForm={basic}
        />
      )}
      {modalStatusReady && (
        <ResponseModal
          title='Response Details'
          open={isResponseModalOpen}
          onClose={() => setIsResponseModalOpen(false)}
          responseData={selectedResponseData}
          fields={response}
          optionsMap={optionsMap}
        />
      )}
      {displayBtn && (
        <Box display="flex" alignItems="center" gap={1}>
          <Button variant="contained" color="primary" onClick={fetchData}
                  startIcon={<FontAwesomeIcon icon={faRefresh} style={{ width: 16, height: 16 }} spin={loading} />}
          >
            Refresh data
          </Button>
          <Button variant="outlined" onClick={handleEdit} title="Edit this Entry Summary">
            Edit
          </Button>
        </Box>
      )}
    </>
  )
}

export { StatusResponse }
